import Drawer from '@material-ui/core/Drawer';
import {monthRoundedTime} from '@wandb/weave/common/util/time';
import {Button} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {Pill, Tag} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';

import {useRunOverviewLineageQuery} from '../../generated/graphql';
import {DateFormat, format} from '../../util/date';
import * as urls from '../../util/urls';
// TODO: change to common component once thats released
import {ResizableDrawer} from '../Registries/workflows/ResizableDrawer';
import {InstrumentedLoader as Loader} from '../utility/InstrumentedLoader';

export type runNodeInputType = {
  entityName: string;
  projectName: string;
  runName: string;
  runDisplayName: string;
  jobType: string;
};

type InfoDrawerRunsProps = {
  open: boolean;
  closeDrawer: () => void;
  runNodeInput: runNodeInputType;
};

export const InfoDrawerRuns = ({
  open,
  closeDrawer,
  runNodeInput,
}: InfoDrawerRunsProps) => {
  const [width, setWidth] = useState(600); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };

  const {entityName, projectName, runName, runDisplayName, jobType} =
    runNodeInput;

  const {data, loading} = useRunOverviewLineageQuery({
    variables: {
      entityName,
      projectName,
      runName,
    },
  });

  if (loading) {
    return (
      <Loader
        name="lineage-dag-run-node-info"
        inline
        size="tiny"
        samplingRate={1}
      />
    );
  }

  if (!loading && data == null) {
    return null;
  }

  const runResults = data?.project?.run;
  const noProjectAccess = runResults?.project?.name == null;

  const config = JSON.parse(
    runResults?.config != null ? runResults?.config : '{}'
  );
  let cliVersion;
  if (
    config != null &&
    (((config || {})._wandb || {}).value || {}).cli_version
  ) {
    cliVersion = config._wandb.value.cli_version;
  }

  return (
    <Drawer anchor="right" open={open} onClose={closeDrawer}>
      <ResizableDrawer
        startWidth={width}
        minWidth={600}
        onSetWidth={onSetWidth}>
        <Tailwind style={{width: '100%', height: '100%'}}>
          <div className="h-full w-full px-24 pb-20 pt-60">
            <div className="flex h-full w-full flex-col">
              <div className="flex items-center">
                <div className="flex flex-row py-20 text-xl font-semibold leading-8">
                  <div
                    className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full
                    bg-[#A1F0CB]/50">
                    <Icon
                      className="h-[25px] w-[25px] text-green-600"
                      name="running-repeat"
                    />
                  </div>
                  <div className="ml-[8px] flex break-all">
                    {runDisplayName}
                  </div>
                </div>

                <div className="flex-1 text-right">
                  <Button
                    onClick={closeDrawer}
                    icon="close"
                    size="large"
                    variant="ghost"
                  />
                </div>
              </div>

              <div className="-mx-20 h-1 bg-moon-250"></div>

              <div className="pb-300 w-full flex-1 overflow-y-auto p-4">
                <div className="mb-4 mt-24 flex items-center">
                  <div>
                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          Run path
                        </div>
                      </div>
                      {noProjectAccess ? (
                        <div className="break-all font-semibold">{runName}</div>
                      ) : (
                        <Link
                          key={`${runDisplayName}`}
                          to={urls.run({
                            entityName,
                            projectName,
                            name: runName,
                          })}>
                          <div className="break-all font-semibold text-teal-600">
                            {entityName}/{projectName}/{runName}
                          </div>
                        </Link>
                      )}
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] text-moon-550">
                          Description
                        </div>
                      </div>
                      <div
                        className={`break-words ${
                          runResults?.notes == null ? 'moon-400' : 'black'
                        }`}>
                        {runResults?.notes == null ? '--' : runResults?.notes}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          Tags
                        </div>
                      </div>
                      <div className="gap-y-5px flex flex-wrap">
                        {runResults?.tags != null &&
                          runResults?.tags.map(tag => {
                            return (
                              // eslint-disable-next-line react/jsx-key
                              <div className="mr-[5px]">
                                <Tag color={'teal'} label={tag} />
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          Author
                        </div>
                      </div>
                      {runResults?.user?.username != null && (
                        <Link
                          key={`${runResults?.user?.username}`}
                          to={urls.profilePage(runResults?.user?.username)}>
                          <div className="font-semibold text-teal-600">
                            {runResults?.user?.username}
                          </div>
                        </Link>
                      )}
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          State
                        </div>
                      </div>
                      <div className="break-words">
                        {runResults?.state != null && (
                          <Pill
                            icon={
                              runResults?.state === 'finished'
                                ? 'checkmark-circle'
                                : 'failed'
                            }
                            color={
                              runResults?.state === 'finished' ? 'green' : 'red'
                            }
                            label={
                              runResults?.state[0].toUpperCase() +
                              runResults?.state.slice(1)
                            }
                          />
                        )}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px] text-moon-550">
                        Start time
                      </div>
                      <div>
                        {runResults?.createdAt != null &&
                          format(
                            new Date(runResults?.createdAt),
                            DateFormat.MONTH_DAYTH_YEAR_24HOUR_MIN_SECONDS,
                            'UTC'
                          )}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          Duration
                        </div>
                      </div>
                      <div className="break-words">
                        {monthRoundedTime(
                          runResults?.computeSeconds != null
                            ? runResults?.computeSeconds
                            : 0
                        )}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          Hostname
                        </div>
                      </div>
                      <div
                        className={`break-words ${
                          runResults?.host == null ? 'moon-400' : 'black'
                        }`}>
                        {runResults?.host == null ? '--' : runResults?.host}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          OS
                        </div>
                      </div>
                      <div
                        className={`break-words ${
                          runResults?.runInfo?.os == null ? 'moon-400' : 'black'
                        }`}>
                        {runResults?.runInfo?.os == null
                          ? '--'
                          : runResults?.runInfo?.os}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          Python version
                        </div>
                      </div>
                      <div className="break-words">
                        {runResults?.runInfo?.python}
                      </div>
                    </div>

                    {runResults?.group && (
                      <div className="flex pb-[14px]">
                        <div className="mr-[80px] w-[136px]">
                          <div className="mr-[80px] w-[136px] text-moon-550">
                            Group
                          </div>
                        </div>
                        <div className="break-words">{runResults?.group}</div>
                      </div>
                    )}

                    {jobType && (
                      <div className="flex pb-[14px]">
                        <div className="mr-[80px] w-[136px]">
                          <div className="mr-[80px] w-[136px] text-moon-550">
                            Job type
                          </div>
                        </div>
                        <div className="break-words">{jobType}</div>
                      </div>
                    )}

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          W&B CLI Version
                        </div>
                      </div>
                      <div>{cliVersion}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="-mx-20 h-1 bg-moon-250"></div>
              <div className="flex items-center pt-20">
                <Button
                  className="w-full"
                  variant="primary"
                  size="large"
                  onClick={closeDrawer}>
                  {'Done'}
                </Button>
              </div>
            </div>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};
